import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, ChevronLeft, ChevronRight, Clock, Loader2, Plus, Send, ShieldCheck, Sparkles, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CandidateDrawer } from '@/lib/CandidateDrawer'
import { type BatchStatus, type JobDetail, type JobRow, type ScreenedCandidate, getBatchScreen, getJob, listJobs, screenStreamUrl, startBatchScreen } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { Avatar, PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/screen')({
  component: ScreenPage,
  // requirements live on a Job — screening always runs an existing job via ?jobId
  validateSearch: (s: Record<string, unknown>): { jobId?: string } => ({ jobId: typeof s.jobId === 'string' ? s.jobId : undefined }),
})

interface StepState {
  key: string
  label: string
  status: 'running' | 'done'
  detail?: string
}

const fmtExp = (m: number | null | undefined) => (m == null ? '—' : `${Math.floor(m / 12)}y ${m % 12}m`)
const verdictVariant = (v: string | null): 'success' | 'warning' | 'error' | 'info' =>
  v === 'strong' ? 'success' : v === 'weak' ? 'error' : v === 'maybe' ? 'warning' : 'info'

function ScreenPage() {
  const { jobId } = Route.useSearch()
  const navigate = useNavigate()
  const [job, setJob] = useState<JobDetail | null>(null)
  const [loadingJob, setLoadingJob] = useState(false)
  const [running, setRunning] = useState(false)
  const [steps, setSteps] = useState<StepState[]>([])
  const [cands, setCands] = useState<ScreenedCandidate[]>([])
  const [gate, setGate] = useState<string | null>(null)
  const [approved, setApproved] = useState(false)
  const [autopilot, setAutopilot] = useState(false)
  const [openCand, setOpenCand] = useState<ScreenedCandidate | null>(null)
  const [minScore, setMinScore] = useState(55) // 55 = "maybe and up" (calibrated models rarely score a domain-mismatched pool ≥80)
  const [slPage, setSlPage] = useState(0)
  const [mode, setMode] = useState<'standard' | 'economy'>('standard')
  const [batch, setBatch] = useState<BatchStatus | null>(null)
  const esRef = useRef<EventSource | null>(null)

  // Economy mode: poll the batch status until it stores results (then the shortlist lives on the job page).
  useEffect(() => {
    if (!batch || !jobId) return
    if (['completed', 'failed', 'expired', 'cancelled'].includes(batch.status)) return
    const t = setInterval(() => { getBatchScreen(jobId).then((r) => r.batch && setBatch(r.batch)).catch(() => {}) }, 20_000)
    return () => clearInterval(t)
  }, [batch, jobId])

  useEffect(() => () => esRef.current?.close(), [])
  useEffect(() => setSlPage(0), [minScore, cands.length])

  // load the job we're screening (requirements come from it, read-only)
  useEffect(() => {
    esRef.current?.close()
    setSteps([]); setCands([]); setGate(null); setApproved(false); setRunning(false)
    if (!jobId) {
      setJob(null)
      return
    }
    setLoadingJob(true)
    getJob(jobId)
      .then(setJob)
      .catch(() => toast.error('Could not load that job.'))
      .finally(() => setLoadingJob(false))
  }, [jobId])

  const run = useCallback(() => {
    if (!jobId) return
    esRef.current?.close()
    setSteps([]); setCands([]); setGate(null); setApproved(false); setRunning(true)
    const es = new EventSource(screenStreamUrl(jobId))
    esRef.current = es
    es.onmessage = (ev) => {
      const e = JSON.parse(ev.data)
      if (e.type === 'step') {
        setSteps((prev) => {
          const i = prev.findIndex((s) => s.key === e.key)
          const s: StepState = { key: e.key, label: e.label, status: e.status, detail: e.detail }
          if (i >= 0) {
            const c = [...prev]
            c[i] = s
            return c
          }
          return [...prev, s]
        })
      } else if (e.type === 'candidate') {
        if (e.candidate) setCands((prev) => [...prev, e.candidate])
      } else if (e.type === 'shortlist') {
        const list = e.shortlist ?? e.candidates // server sends the final sorted list under `shortlist`
        if (Array.isArray(list)) setCands(list) // replaces the streamed-in cards with the complete ordered set
      } else if (e.type === 'gate') {
        setGate(e.message)
      } else if (e.type === 'done') {
        setRunning(false)
        setJob((j) => (j ? { ...j, screened: true } : j)) // the role is now screened — reflect it immediately
        es.close()
      } else if (e.type === 'error') {
        toast.error(e.message)
        setRunning(false)
        es.close()
      }
    }
    es.onerror = () => {
      es.close()
      setRunning(false)
    }
  }, [jobId])

  const queueEconomy = useCallback(async () => {
    if (!jobId) return
    try {
      await startBatchScreen(jobId, { topN: 25 })
      const r = await getBatchScreen(jobId)
      setBatch(r.batch)
      toast.success('Queued overnight — results within 24h at ~50% fewer credits.')
    } catch {
      toast.error('Could not queue the batch. Try again.')
    }
  }, [jobId])

  const approve = () => { setApproved(true); setGate(null); toast.success('Shortlist approved — sent to the client portal') }
  const goAutopilot = () => { setAutopilot(true); setApproved(true); setGate(null); toast('Autopilot ON — review gates will auto-approve', { icon: '⚡' }) }

  // score filter (default ≥80) + 10-per-page pagination over the shortlist
  const filtered = cands.filter((c) => (c.score ?? 0) >= minScore)
  const slLast = Math.max(0, Math.ceil(filtered.length / 10) - 1)
  const slPageRows = filtered.slice(slPage * 10, slPage * 10 + 10)

  return (
    <DashboardLayout title="Live Screening" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Live Screening — Autopilot"
        subtitle="Pick a role and watch the system source, filter, score, and AI-screen candidates in real time."
      />

      {/* No job chosen → pick one (requirements are defined on the Jobs page) */}
      {!jobId ? (
        <JobPicker onPick={(id) => navigate({ to: '/screen', search: { jobId: id } })} />
      ) : loadingJob || !job ? (
        <div className="text-sm text-muted-foreground">Loading job…</div>
      ) : (
        <>
          {/* Job context — requirements read-only, defined on the Jobs page */}
          <Card className="mb-5">
            <CardContent className="pt-5 pb-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 className="text-base font-semibold text-foreground">{job.requirements.title}</h2>
                    {job.screened ? <Badge variant="success">Screened</Badge> : <Badge variant="warning">Pending</Badge>}
                    <Link to="/jobs" className="text-xs text-muted-foreground underline hover:text-foreground">← Jobs</Link>
                  </div>
                  {(job.company || job.requirements.location) && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{[job.company, job.requirements.location].filter(Boolean).join(' · ')}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {job.requirements.mustHave.map((s) => (
                      <span key={s} className="inline-flex items-center rounded border border-info/40 bg-info/10 px-2 py-0.5 text-xs text-info">{s}<span className="ml-1 opacity-60">must</span></span>
                    ))}
                    {(job.requirements.niceToHave ?? []).map((s) => (
                      <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">{s}</span>
                    ))}
                  </div>
                </div>
                {/* Controls: full-width stacked on phones, a tidy right-aligned column on md+ (never collides
                    with the skills — the role block shrinks and wraps its chips instead). */}
                <div className="flex w-full shrink-0 flex-col gap-2 md:w-auto md:items-end">
                  {/* Mode: Standard (instant) vs Economy (overnight Groq Batch, ~50% fewer credits) */}
                  <div className="flex w-full flex-col rounded-md border border-input bg-card p-0.5 text-xs sm:flex-row md:w-auto">
                    <button onClick={() => setMode('standard')} className={`whitespace-nowrap rounded px-2.5 py-1 font-medium transition-colors sm:flex-1 md:flex-none ${mode === 'standard' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}>Standard · instant</button>
                    <button onClick={() => setMode('economy')} className={`whitespace-nowrap rounded px-2.5 py-1 font-medium transition-colors sm:flex-1 md:flex-none ${mode === 'economy' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}>Economy · overnight ½ credits</button>
                  </div>
                  {mode === 'standard' ? (
                    <Button onClick={run} disabled={running} className="w-full md:w-auto">
                      {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                      {running ? 'Screening…' : job.screened ? 'Re-run screening' : 'Run live screening'}
                    </Button>
                  ) : (
                    <Button onClick={queueEconomy} disabled={!!batch && !['completed', 'failed'].includes(batch.status)} className="w-full md:w-auto">
                      <Clock className="mr-1.5 h-4 w-4" /> {batch && !['completed', 'failed'].includes(batch.status) ? 'Queued…' : 'Queue overnight screening'}
                    </Button>
                  )}
                </div>
              </div>

              {batch && (
                <div className={`mt-4 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${batch.status === 'completed' ? 'border-success/40 bg-success/5 text-success' : batch.status === 'failed' ? 'border-error/40 bg-error/5 text-error' : 'border-info/40 bg-info/5 text-info'}`}>
                  <Clock className="h-3.5 w-3.5" />
                  {batch.status === 'completed' ? (
                    <>Overnight screening complete — <Link to="/jobs/$jobId" params={{ jobId: jobId! }} className="font-medium underline">view the shortlist</Link>.</>
                  ) : batch.status === 'failed' ? (
                    <>Batch hit a transient limit — auto-retrying. {batch.counts ? `(${batch.counts.failed ?? 0}/${batch.counts.total ?? 0} failed)` : ''}</>
                  ) : (
                    <>Overnight screening <span className="font-medium">{batch.status.replace('_', ' ')}</span> — {batch.total} candidates via {batch.model ?? 'batch'} · results within 24h. You can leave this page.</>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Horizontal workflow stepper */}
          {steps.length > 0 && (
            <Card className="mb-5">
              <CardContent className="pt-5 pb-4">
                <h2 className="mb-4 flex items-center gap-2 text-sm font-medium">
                  <Zap className="h-4 w-4 text-primary" /> Workflow
                  {autopilot && <Badge variant="info">autopilot</Badge>}
                </h2>
                <ol className="flex items-start">
                  {steps.map((s, i) => (
                    <Fragment key={s.key}>
                      <motion.li initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex flex-1 flex-col items-center px-1 text-center">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-card">
                          {s.status === 'done' ? <CheckCircle2 className="h-5 w-5 text-success" /> : <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                        </span>
                        <span className="mt-2 text-xs font-medium leading-tight text-foreground">{s.label}</span>
                        {s.detail && <span className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{s.detail}</span>}
                      </motion.li>
                      {i < steps.length - 1 && <div className="mt-3.5 h-0.5 flex-1 rounded bg-border" />}
                    </Fragment>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* Shortlist table */}
          {cands.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center gap-2 px-4 pt-4 pb-3">
                  <span className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-primary" /> Shortlist</span>
                  <span className="text-xs text-muted-foreground">{filtered.length} of {cands.length} ≥ {minScore} · click a row to view</span>
                  <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
                    Min score
                    <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} className="h-8 rounded-md border border-input bg-card px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring" aria-label="Minimum score">
                      {[0, 55, 70, 80, 90].map((v) => (
                        <option key={v} value={v}>{v === 0 ? 'All' : v === 55 ? 'Maybe+ (55)' : v === 80 ? 'Strong (80)' : `${v}+`}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-y border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="px-4 py-2.5 font-medium">#</th>
                        <th className="px-4 py-2.5 font-medium">Candidate</th>
                        <th className="px-4 py-2.5 font-medium">Fit</th>
                        <th className="px-4 py-2.5 font-medium">Company</th>
                        <th className="px-4 py-2.5 font-medium">Exp</th>
                        <th className="px-4 py-2.5 font-medium">Location</th>
                        <th className="px-4 py-2.5 font-medium">Skills</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <AnimatePresence>
                        {slPageRows.length === 0 && (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No candidates score ≥ {minScore}. Lower the minimum score to see more.</td></tr>
                        )}
                        {slPageRows.map((c, i) => (
                          <motion.tr key={c.candidateId} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={() => jobId && navigate({ to: '/compare/$jobId/$candidateId', params: { jobId, candidateId: c.candidateId } })} className="cursor-pointer transition-colors hover:bg-muted/40">
                            <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{slPage * 10 + i + 1}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2.5">
                                <Avatar name={c.name ?? '?'} className="h-7 w-7 shrink-0 text-[10px]" />
                                <div className="min-w-0">
                                  <div className="font-medium text-foreground">{c.name ?? '—'}</div>
                                  <div className="max-w-[280px] truncate text-xs text-muted-foreground">{c.rationale?.replace(/\*\*/g, '')}</div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                <Badge variant={verdictVariant(c.verdict)}>{c.verdict ?? 'scored'}</Badge>
                                <span className="font-semibold tabular-nums">{c.score}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground">{c.employer ?? '—'}</td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{fmtExp(c.totalMonthsExp)}</td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{c.location ?? '—'}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex max-w-[240px] flex-wrap gap-1">
                                {(c.skills ?? []).slice(0, 3).map((s) => (
                                  <span key={s} className="inline-flex items-center rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] text-muted-foreground">{s}</span>
                                ))}
                                {(c.skills?.length ?? 0) > 3 && <span className="px-1 text-[11px] text-muted-foreground">+{(c.skills?.length ?? 0) - 3}</span>}
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
                {filtered.length > 10 && (
                  <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
                    <span>Showing {slPage * 10 + 1}–{Math.min((slPage + 1) * 10, filtered.length)} of {filtered.length}</span>
                    <div className="flex items-center gap-1">
                      <button disabled={slPage === 0} onClick={() => setSlPage((p) => Math.max(0, p - 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50"><ChevronLeft className="h-3.5 w-3.5" /> Prev</button>
                      <span className="px-1.5">Page {slPage + 1} of {slLast + 1}</span>
                      <button disabled={slPage >= slLast} onClick={() => setSlPage((p) => Math.min(slLast, p + 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50">Next <ChevronRight className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Human-review gate */}
          <AnimatePresence>
            {gate && !approved && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                <Card className="border-primary/40 bg-primary/5">
                  <CardContent className="flex flex-wrap items-center gap-4 pt-5 pb-5">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">Recruiter review</div>
                      <div className="text-xs text-muted-foreground">{gate}</div>
                    </div>
                    <Button onClick={approve}><Send className="mr-1.5 h-4 w-4" /> Approve & send to client</Button>
                    <Button onClick={goAutopilot}><Zap className="mr-1.5 h-4 w-4" /> Switch to autopilot</Button>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {approved && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5 flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" /> Shortlist approved {autopilot ? '(autopilot)' : ''} — delivered to the client portal.
            </motion.div>
          )}
        </>
      )}

      <CandidateDrawer cand={openCand} onClose={() => setOpenCand(null)} />
    </DashboardLayout>
  )
}

function JobPicker({ onPick }: { onPick: (id: string) => void }) {
  const [jobs, setJobs] = useState<JobRow[]>([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    listJobs({ sort: 'recent', pageSize: 8 }).then((r) => setJobs(r.jobs)).finally(() => setLoading(false))
  }, [])
  return (
    <Card>
      <CardContent className="pt-5 pb-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium">Choose a role to screen</h2>
          <Button size="sm" variant="outline" asChild><Link to="/jobs"><Plus className="mr-1.5 h-3.5 w-3.5" /> New job</Link></Button>
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading roles…</div>
        ) : jobs.length === 0 ? (
          <div className="text-sm text-muted-foreground">No jobs yet — create one on the <Link to="/jobs" className="text-primary underline">Jobs</Link> page, then screen it.</div>
        ) : (
          <div className="divide-y divide-border">
            {jobs.map((j) => (
              <button key={j.id} onClick={() => onPick(j.id)} className="flex w-full items-center gap-3 px-1 py-2.5 text-left transition-colors hover:bg-muted/40">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground">{j.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{[...j.mustHave, ...j.niceToHave].slice(0, 5).join(' · ')}</div>
                </div>
                {j.screened ? <Badge variant="success">Screened</Badge> : <Badge variant="warning">Pending</Badge>}
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
